'use client';
import { forwardRef, type HTMLAttributes, type PropsWithChildren } from 'react';
import { FileDropZone } from '@/app/components/shared/FileDropZone';
import { ACCEPT_VIDEO_MIMETYPE, MAX_IMAGE_FILE_SIZE, MAX_VIDEO_FILE_SIZE, UPLOAD_SCENE } from '@/app/constants';
import { useHasAnyModalOpen } from '@/app/hooks';
import { cn } from '@/app/utils';
import { type UseMediaUploadOptions, useMediaUpload } from './hooks/use-media-upload';

export type MediaDropZoneProps = Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> &
  UseMediaUploadOptions & {
    /** 为 true 时，组件内部输入框/编辑器获得焦点后可通过粘贴上传剪贴板文件。 */
    enablePaste?: boolean;
    /** 是否启用 document 级全屏拖拽落点；关闭后仅当前容器响应拖拽和粘贴。 */
    fullscreen?: boolean;
    /** 拖拽上传占位文案。 */
    dropPlaceholder?: string;
    /** 是否显示拖拽上传占位层。 */
    showPlaceholder?: boolean;
    /**
     * 为 true 时不因 `useHasAnyModalOpen` 禁用拖拽。
     * 用于本组件挂在「会触发全局 modal 状态」的弹窗内部时（例如 PostsModal），
     * 否则 guard 恒为 true，fullscreen 蒙层与 document 级拖拽监听都不会生效。
     */
    ignoreModalOpenGuard?: boolean;
  };

/**
 * 媒体上传组件（拖拽区域）
 *
 * 支持拖拽上传图片或视频（及可选音频），根据文件类型自动判断并调用相应的上传逻辑。
 * 可通过 `extraFileAccept` 声明更多 MIME/扩展名，匹配时走自定义 `onFile`（不经媒体上传管线）。
 * 适用于需要在同一区域支持多种素材上传的场景。
 *
 * 注意：此组件只提供拖拽功能，如果需要点击上传功能，请使用 HeadlessMediaDropZone
 *
 */
export const MediaDropZone = forwardRef<HTMLDivElement, PropsWithChildren<MediaDropZoneProps>>(
  (
    {
      className,
      disabled,
      maxCount,
      maxImageCount = 1,
      maxImageFileSize = MAX_IMAGE_FILE_SIZE,
      isInitialSelectedImageRange = true,
      getSelectedImage,
      validateImageResolution,
      maxVideoCount = 1,
      maxVideoDuration,
      minVideoDuration,
      maxVideoSize = MAX_VIDEO_FILE_SIZE,
      validateVideoMetadata,
      scene = UPLOAD_SCENE.VIDU,
      videoScene,
      videoAccept = ACCEPT_VIDEO_MIMETYPE,
      captureCoverImage = false,
      uploadCoverImage = false,
      allowTempMode = false,
      emitUploadingInTempMode,
      emitUploadingPreview,
      maxAudioCount,
      maxAudioDuration,
      minAudioDuration,
      maxAudioSize,
      audioAccept,
      acceptImage = true,
      acceptVideo = false,
      acceptAudio = false,
      extraFileAccept,
      getLimitReachedMessage,
      onChange,
      onMetadataReady,
      onBeforeBatchUpload,
      children,
      enablePaste,
      fullscreen = true,
      dropPlaceholder,
      showPlaceholder,
      ignoreModalOpenGuard = false,
      ...rest
    },
    ref,
  ) => {
    const hasAnyModalOpen = useHasAnyModalOpen();
    const isDisabled = disabled || (!ignoreModalOpenGuard && hasAnyModalOpen);

    const { handleFiles } = useMediaUpload({
      disabled: isDisabled,
      maxCount,
      maxImageCount,
      maxImageFileSize,
      isInitialSelectedImageRange,
      scene,
      videoScene,
      getSelectedImage,
      validateImageResolution,
      maxVideoCount,
      maxVideoDuration,
      minVideoDuration,
      maxVideoSize,
      validateVideoMetadata,
      videoAccept,
      captureCoverImage,
      uploadCoverImage,
      allowTempMode,
      emitUploadingInTempMode,
      emitUploadingPreview,
      maxAudioCount,
      maxAudioDuration,
      minAudioDuration,
      maxAudioSize,
      audioAccept,
      acceptImage,
      acceptVideo,
      acceptAudio,
      extraFileAccept,
      getLimitReachedMessage,
      onChange,
      onMetadataReady,
      onBeforeBatchUpload,
    });

    return (
      <FileDropZone
        ref={ref}
        className={cn(className)}
        onFilesDrop={(files) => {
          if (!files || files.length === 0) return;
          void handleFiles(files);
        }}
        enablePaste={enablePaste}
        fullscreen={fullscreen}
        dropPlaceholder={dropPlaceholder}
        showPlaceholder={showPlaceholder}
        disabled={isDisabled}
        {...rest}
      >
        {children}
      </FileDropZone>
    );
  },
);

MediaDropZone.displayName = 'MediaDropZone';
